import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlphaQuantPanel } from '@/components/AlphaQuantPanel';
import { AlphaQuantSignal, AlphaQuantTrade } from '@/hooks/useAlphaQuantSignals';
import { AlphaQuantChart } from '@/components/AlphaQuantChart';
import { EnrichedCandleData } from '@/services/binanceService';
import { Zap, BarChart3, Activity, Info, TrendingUp } from 'lucide-react';

const AlphaQuant = () => {
  const [signals, setSignals] = useState<AlphaQuantSignal[]>([]);
  const [trades, setTrades] = useState<AlphaQuantTrade[]>([]);
  const [candleData, setCandleData] = useState<EnrichedCandleData[]>([]);
  
  const handleSignalsUpdate = (
    newSignals: AlphaQuantSignal[], 
    newTrades: AlphaQuantTrade[],
    data?: EnrichedCandleData[]
  ) => {
    setSignals(newSignals);
    setTrades(newTrades);
    if (data) {
      setCandleData(data);
    }
  };
  
  // Count entries and exits
  const tradeStats = useMemo(() => {
    const entries = trades.filter(t => t.type === 'entry');
    const exits = trades.filter(t => t.type === 'exit');
    const wins = exits.filter(t => (t.pnl || 0) > 0).length;
    return { entries: entries.length, exits: exits.length, wins };
  }, [trades]);
  
  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
              <Zap className="h-6 w-6 text-white" />
            </div>
            AlphaQuant Signal
          </h1>
          <p className="text-muted-foreground">
            Estratégia de derivativos baseada em OI, Funding e Price Action com filtros macro
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs gap-1">
            <Activity className="h-3 w-3" />
            Timeframe 1D
          </Badge>
          <Badge variant="outline" className="text-xs gap-1">
            <Info className="h-3 w-3" />
            Binance Futures
          </Badge>
        </div>
      </div>
      
      {/* Info Card */}
      <Card className="bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-red-500/10 border-yellow-500/30">
        <CardContent className="pt-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="h-6 w-6 text-yellow-400" />
            </div>
            <div className="space-y-1">
              <h3 className="font-semibold text-yellow-400">Estratégia Baseada em Pine Script</h3>
              <p className="text-sm text-muted-foreground">
                Implementação fiel do indicador "Alpha Quant Signal — Macro Filters" com 4 tipos de sinais:
                <span className="text-red-400 ml-1 font-medium">L-SQ</span> (Long Squeeze),
                <span className="text-green-400 ml-1 font-medium">REC</span> (Recovery),
                <span className="text-orange-400 ml-1 font-medium">TOP-REV</span> (Top Reversal) e
                <span className="text-blue-400 ml-1 font-medium">BOT-REV</span> (Bottom Reversal).
                Utilizando dados de Open Interest, Funding Rate e Price Action da Binance.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Chart with Trade Markers */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            BTC/USDT — Gráfico Diário com Operações
          </CardTitle>
          <CardDescription>
            {trades.length > 0 
              ? `${tradeStats.entries} entradas · ${tradeStats.exits} saídas · ${tradeStats.wins} wins`
              : 'Execute o backtest para visualizar as operações no gráfico'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="p-2">
          <AlphaQuantChart 
            data={candleData}
            trades={trades}
            height={400}
          />
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Panel */}
        <div className="lg:col-span-2">
          <AlphaQuantPanel onSignalsUpdate={handleSignalsUpdate} />
        </div>
        
        {/* Side Info */}
        <div className="space-y-4">
          {/* Legend */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Legenda de Sinais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/50">
                  L-SQ
                </Badge>
                <div className="text-xs">
                  <span className="font-medium">Long Squeeze</span>
                  <p className="text-muted-foreground">OI↑ + Price band + Funding → SHORT</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50">
                  REC
                </Badge>
                <div className="text-xs">
                  <span className="font-medium">Recovery</span>
                  <p className="text-muted-foreground">OI↓ + Price hold + Funding → LONG</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-orange-500/20 text-orange-400 border-orange-500/50">
                  TOP-REV
                </Badge>
                <div className="text-xs">
                  <span className="font-medium">Top Reversal</span>
                  <p className="text-muted-foreground">Price↑ + Diff↓ + Trigger → SHORT</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/50">
                  BOT-REV
                </Badge>
                <div className="text-xs">
                  <span className="font-medium">Bottom Reversal</span>
                  <p className="text-muted-foreground">Price↓ + Diff↑ + Trigger → LONG</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Methodology */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Metodologia</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-2">
              <p>
                <strong className="text-foreground">Open Interest:</strong> Monitora variação de OI para identificar 
                acumulação/distribuição de posições.
              </p>
              <p>
                <strong className="text-foreground">Funding Rate:</strong> Analisa o custo de carry para identificar 
                desequilíbrios entre longs e shorts.
              </p>
              <p>
                <strong className="text-foreground">Diff (Price - OI):</strong> Divergência entre preço e OI para 
                detectar reversões potenciais.
              </p>
              <p>
                <strong className="text-foreground">Triggers:</strong> Condições de confirmação para entrada 
                (cruzamentos, flushes, etc.).
              </p>
            </CardContent>
          </Card>
          
          {/* Limitations */}
          <Card className="border-yellow-500/30 bg-yellow-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-yellow-400">⚠️ Limitações</CardTitle>
            </CardHeader>
            <CardContent className="text-xs text-muted-foreground space-y-2">
              <p>
                • API da Binance retorna apenas <strong className="text-foreground">30 dias</strong> de histórico de OI
              </p>
              <p>
                • Backtest limitado ao período disponível
              </p>
              <p>
                • Dados em tempo real requerem polling (não WebSocket)
              </p>
              <p>
                • Filtros NFCI/CS Alpha aplicados quando disponíveis
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      
    </div>
  );
};

export default AlphaQuant;
